/**
 * Load Visualization Utilities
 * 
 * Shared utilities for rendering load arrows and visualizations across all load types.
 * Ensures consistent sizing, scaling, and appearance.
 */

import * as THREE from 'three';
import { Vector3 } from 'three';

/**
 * Configuration for arrow sizes and zoom behavior
 */
export const LOAD_VISUAL_CONFIG = {
  // Base sizes (at zoom scale = 1)
  base: {
    arrowHeadLength: 0.25,
    arrowHeadWidth: 0.30,
    arrowTailLength: 1.0,
    arrowTailThickness: 0.010,
    circularArcRadius: 0.35,
    circularArcTubeRadius: 0.015,
  },
  
  // Zoom behavior
  zoom: {
    referenceDistance: 10,      // Perspective camera reference
    orthoReferenceZoom: 100,     // Orthographic camera reference
    minScale: 0.5,
    maxScale: 4,
  },
  
  // Colors
  colors: {
    x: '#FF0000',        // Red for X-axis
    y: '#00FF00',        // Green for Y-axis  
    z: '#0000FF',        // Blue for Z-axis
    twisted: '#9333EA',  // Purple for twisted/torsional loads
  },
} as const;

/**
 * Compute zoom-adaptive scale so on-screen size stays readable
 * Matches behavior across all load visualizations
 */
export function getZoomScale(camera: THREE.Camera, worldPos: Vector3): number {
  const cfg = LOAD_VISUAL_CONFIG.zoom;
  let scale = 1;
  
  // @ts-ignore
  if ((camera as any).isPerspectiveCamera) {
    const cam = camera as THREE.PerspectiveCamera;
    const distance = cam.position.clone().sub(worldPos).length();
    scale = distance / cfg.referenceDistance;
  } else {
    // Orthographic: inverse-proportional to zoom
    // @ts-ignore
    const cam = camera as any;
    const zoom = cam.zoom ?? cfg.orthoReferenceZoom;
    scale = cfg.orthoReferenceZoom / zoom;
  }
  
  return Math.min(cfg.maxScale, Math.max(cfg.minScale, scale));
}

/**
 * Compute local beam coordinate system
 * X-axis: along beam, Y & Z: perpendicular (right-handed)
 */
export function computeBeamLocalAxes(startPoint: Vector3, endPoint: Vector3) {
  const x = endPoint.clone().sub(startPoint).normalize();
  
  // Choose perpendicular reference that's not parallel to beam
  const worldUp = new THREE.Vector3(0, 1, 0);
  const tempRef = Math.abs(x.dot(worldUp)) > 0.95 
    ? new THREE.Vector3(0, 0, 1) 
    : worldUp;
  
  const z = new THREE.Vector3().crossVectors(x, tempRef).normalize();
  const y = new THREE.Vector3().crossVectors(z, x).normalize();
  
  return { x, y, z };
}

/**
 * Get perpendicular direction in beam's YZ plane that's most visible to camera
 * Used for consistent arrow orientation
 */
export function chooseMostVisiblePerpendicular(
  beamDirection: Vector3,
  localY: Vector3,
  localZ: Vector3,
  camera?: THREE.Camera
): Vector3 {
  const candidates: Vector3[] = [];
  
  // Build candidates orthogonal to beam direction
  if (Math.abs(localY.dot(beamDirection)) < 0.99) {
    candidates.push(localY.clone());
  }
  if (Math.abs(localZ.dot(beamDirection)) < 0.99) {
    candidates.push(localZ.clone());
  }
  
  // Fallback if no candidates
  if (candidates.length === 0) {
    const fallback = Math.abs(beamDirection.y) < 0.99 
      ? new THREE.Vector3(0, 1, 0) 
      : new THREE.Vector3(1, 0, 0);
    const ortho = new THREE.Vector3().crossVectors(beamDirection, fallback).normalize();
    candidates.push(ortho);
  }
  
  // If camera available, choose most visible (most orthogonal to view)
  if (camera) {
    const viewDir = new THREE.Vector3();
    camera.getWorldDirection(viewDir);
    viewDir.normalize();
    
    candidates.sort((a, b) => 
      Math.abs(a.dot(viewDir)) - Math.abs(b.dot(viewDir))
    );
  }
  
  return candidates[0].normalize();
}

/**
 * Create rotation quaternion to align arrow cone with direction
 */
export function createArrowQuaternion(direction: Vector3): THREE.Quaternion {
  const coneUp = new THREE.Vector3(0, 1, 0); // Default cone points up
  return new THREE.Quaternion().setFromUnitVectors(coneUp, direction);
}

/**
 * Generate arc points for circular arrow (used in twisted loads)
 * @param center Center point of arc
 * @param radius Arc radius
 * @param perpDir1 First perpendicular direction
 * @param perpDir2 Second perpendicular direction  
 * @param angleRadians Total angle to sweep (e.g., 3π/2 for 270°)
 * @param clockwise Direction of sweep
 * @param segments Number of line segments for smoothness
 */
export function generateArcPoints(
  center: Vector3,
  radius: number,
  perpDir1: Vector3,
  perpDir2: Vector3,
  angleRadians: number,
  clockwise: boolean,
  segments: number = 48
): Vector3[] {
  const points: Vector3[] = [];
  const dirMultiplier = clockwise ? 1 : -1;
  
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * angleRadians * dirMultiplier;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    
    const point = new THREE.Vector3()
      .copy(center)
      .addScaledVector(perpDir1, x)
      .addScaledVector(perpDir2, y);
    
    points.push(point);
  }
  
  return points;
}
